import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Edit, Trash2, UserCircle, Mail, Building2 } from 'lucide-react';
import { getRolDisplay } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'admin_empresa', label: 'Administrador Empresa' },
    { value: 'finanzas', label: 'Finanzas / Cobranza' },
    { value: 'ejecutivo', label: 'Ejecutivo Comercial' },
    { value: 'solo_lectura', label: 'Solo Lectura' }
];

const Usuarios = () => {
    const { canManageUsers, isSuperAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUsuario, setEditingUsuario] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        nombre: '',
        apellido: '',
        telefono: '',
        rol: 'solo_lectura',
        empresa_id: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usuariosRes, empresasRes] = await Promise.all([
                axios.get(`${API_URL}/api/usuarios`),
                isSuperAdmin() ? axios.get(`${API_URL}/api/empresas`) : Promise.resolve({ data: [] })
            ]);
            setUsuarios(usuariosRes.data);
            setEmpresas(empresasRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (usuario = null) => {
        if (usuario) {
            setEditingUsuario(usuario);
            setFormData({
                email: usuario.email,
                password: '',
                nombre: usuario.nombre,
                apellido: usuario.apellido || '',
                telefono: usuario.telefono || '',
                rol: usuario.rol,
                empresa_id: usuario.empresa_id || ''
            });
        } else {
            setEditingUsuario(null);
            setFormData({
                email: '',
                password: '',
                nombre: '',
                apellido: '',
                telefono: '',
                rol: 'solo_lectura',
                empresa_id: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSend = { ...formData };
            
            // Handle empty empresa_id
            if (!dataToSend.empresa_id) {
                dataToSend.empresa_id = null;
            }

            if (editingUsuario) {
                const updateData = { ...dataToSend };
                delete updateData.password;
                delete updateData.email;
                
                await axios.put(`${API_URL}/api/usuarios/${editingUsuario.id}`, updateData);
                toast.success('Usuario actualizado');
            } else {
                await axios.post(`${API_URL}/api/usuarios`, dataToSend);
                toast.success('Usuario creado');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving usuario:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar usuario');
        }
    };

    const handleDelete = async (usuario) => {
        if (!window.confirm(`¿Está seguro de eliminar a ${usuario.nombre}?`)) return;
        
        try {
            await axios.delete(`${API_URL}/api/usuarios/${usuario.id}`);
            toast.success('Usuario eliminado');
            fetchData();
        } catch (error) {
            console.error('Error deleting usuario:', error);
            toast.error('Error al eliminar usuario');
        }
    };

    const getRolBadgeColor = (rol) => {
        const colors = {
            superadmin: 'bg-purple-100 text-purple-700',
            admin_empresa: 'bg-[#D5E8E8] text-[#3D7A7A]',
            finanzas: 'bg-emerald-100 text-emerald-700',
            ejecutivo: 'bg-amber-100 text-amber-700',
            solo_lectura: 'bg-slate-100 text-slate-700'
        };
        return colors[rol] || 'bg-slate-100 text-slate-700';
    };

    const getEmpresaNombre = (empresaId) => {
        const empresa = empresas.find(e => e.id === empresaId);
        return empresa ? empresa.razon_social : '-';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Usuarios</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Gestión de usuarios del sistema
                    </p>
                </div>
                {canManageUsers() && (
                    <Button onClick={() => handleOpenDialog()} data-testid="new-usuario-btn">
                        <Plus size={18} className="mr-2" />
                        Nuevo Usuario
                    </Button>
                )}
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : usuarios.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <UserCircle size={48} className="text-slate-300 mb-2" />
                            <p>No hay usuarios registrados</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-left py-3 px-4">Usuario</th>
                                        <th className="text-left py-3 px-4 hidden md:table-cell">Email</th>
                                        {isSuperAdmin() && (
                                            <th className="text-left py-3 px-4 hidden lg:table-cell">Empresa</th>
                                        )}
                                        <th className="text-center py-3 px-4">Rol</th>
                                        <th className="text-center py-3 px-4">Estado</th>
                                        <th className="text-right py-3 px-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuarios.map((usuario) => (
                                        <tr key={usuario.id} className="table-row">
                                            <td className="table-cell">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-[#D5E8E8] rounded-full flex items-center justify-center">
                                                        <span className="text-[#5B9A9A] font-medium text-sm">
                                                            {usuario.nombre?.charAt(0) || 'U'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">
                                                            {usuario.nombre} {usuario.apellido}
                                                        </p>
                                                        <p className="text-xs text-slate-500 md:hidden">
                                                            {usuario.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell hidden md:table-cell">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <Mail size={14} />
                                                    {usuario.email}
                                                </div>
                                            </td>
                                            {isSuperAdmin() && (
                                                <td className="table-cell hidden lg:table-cell">
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <Building2 size={14} />
                                                        {getEmpresaNombre(usuario.empresa_id)}
                                                    </div>
                                                </td>
                                            )}
                                            <td className="table-cell text-center">
                                                <Badge className={getRolBadgeColor(usuario.rol)}>
                                                    {getRolDisplay(usuario.rol)}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-center">
                                                <Badge className={usuario.activo ? 'badge-success' : 'badge-error'}>
                                                    {usuario.activo ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-right">
                                                {canManageUsers() && usuario.rol !== 'superadmin' && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon">
                                                                <MoreVertical size={16} />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => handleOpenDialog(usuario)}>
                                                                <Edit size={16} className="mr-2" />
                                                                Editar
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDelete(usuario)}
                                                                className="text-red-600"
                                                            >
                                                                <Trash2 size={16} className="mr-2" />
                                                                Eliminar
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            {!editingUsuario && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            required
                                            data-testid="input-email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Contraseña *</Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            required
                                            data-testid="input-password"
                                        />
                                    </div>
                                </>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nombre">Nombre *</Label>
                                    <Input
                                        id="nombre"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                                        required
                                        data-testid="input-nombre"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="apellido">Apellido</Label>
                                    <Input
                                        id="apellido"
                                        value={formData.apellido}
                                        onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="telefono">Teléfono</Label>
                                <Input
                                    id="telefono"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                                />
                            </div>
                            
                            {/* Empresa selector - only for superadmin */}
                            {isSuperAdmin() && (
                                <div className="space-y-2 pt-4 mt-4 border-t border-slate-200">
                                    <Label htmlFor="empresa_id" className="flex items-center gap-2">
                                        <Building2 size={14} />
                                        Empresa *
                                    </Label>
                                    <Select 
                                        value={formData.empresa_id || 'none'} 
                                        onValueChange={(v) => setFormData({...formData, empresa_id: v === 'none' ? '' : v})}
                                    >
                                        <SelectTrigger data-testid="select-empresa">
                                            <SelectValue placeholder="Seleccionar empresa" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sin empresa asignada</SelectItem>
                                            {empresas.map((empresa) => (
                                                <SelectItem key={empresa.id} value={empresa.id}>
                                                    {empresa.razon_social}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">
                                        Seleccione la empresa a la que pertenecerá este usuario
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="rol">Rol *</Label>
                                <Select 
                                    value={formData.rol} 
                                    onValueChange={(v) => setFormData({...formData, rol: v})}
                                >
                                    <SelectTrigger data-testid="select-rol">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((rol) => (
                                            <SelectItem key={rol.value} value={rol.value}>
                                                {rol.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="save-usuario-btn">
                                {editingUsuario ? 'Guardar cambios' : 'Crear usuario'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Usuarios;
